import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, message, Modal, Row, Space, Spin } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import CompanyTicketClassInput from '@/components/FormItems/CompanyTicketClassInput';
import CreateSegmentModal from './CreateSegmentModal';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import { exportService } from '@/services/exportService';
import ExportBtn from '@/components/app/ExportBtn';
import ImportFileModal from '@/components/modals/ImportFileModal';
import { RcFile } from 'antd/lib/upload';
import ImportBtn from '@/components/app/ImportBtn';
import { CompanyTicketClassDto, BaseImportColumn, BaseImportResponse, BaggageTariffDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageBaggageTariff } from '@/hooks/useHasPermissions';

const PAGE_TITLE = 'baggageTariffsPage_Title';

const columns: IColumnType<BaggageTariffDto>[] = [
  {
    title: 'baggageTariffsPage_From',
    dataIndex: 'originStationName',
    ellipsis: true,
  },
  {
    title: 'baggageTariffsPage_To',
    dataIndex: 'destinationStationName',
    ellipsis: true,
  },

  {
    title: 'baggageTariffsPage_FreeBag',
    dataIndex: 'freeBags',
    ellipsis: true,
  },
  {
    title: 'baggageTariffsPage_MaxBag',
    dataIndex: 'maxBagsAllowed',
    ellipsis: true,
  },
  {
    title: 'baggageTariffsPage_TarrifBagage',
    dataIndex: 'standardTariff',
    ellipsis: true,
  },
  {
    title: 'baggageTariffsPage_SecondTariff',
    dataIndex: 'secondTariff',
    ellipsis: true,
  },
  {
    title: 'baggageTariffsPage_TarrifBagageMoto',
    dataIndex: 'bikeTariff',
    ellipsis: true,
  },
  {
    title: 'baggageTariffsPage_TicketClass',
    dataIndex: 'ticketClassName',
    ellipsis: true,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'SELECT', name: 'baseTicketClassId', label: 'baggageTariffsPage_TicketClass', customElm: <CompanyTicketClassInput /> },
  { type: 'TEXT', name: 'searchText', label: 'baggageTariffsPage_SearchText' },
];

const BaggageTariffsPage = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [segmentForm] = Form.useForm();

  const [formVisible, setFormVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displaySegmentModal, setDisplaySegmentModal] = React.useState<boolean>(false);

  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [importCols, setImportCols] = React.useState<BaseImportColumn[]>([]);
  const [displayImportModal, setDisplayImportModal] = React.useState<boolean>(false);
  const canUpdate = useCanManageBaggageTariff();

  React.useEffect(() => {
    apiSdk.BaggageTariffService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);

  const updateEntity = values => {
    setLoading(true);
    apiSdk.BaggageTariffService.saveBaggageTariff({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onExportExcel = (cols: string[]) => {
    exportService
      .exportBaggageTariff({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportBaggageTariff({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toPdf();
  };
  const handleImportClick = () => {
    if (!importCols.length) {
      apiSdk.BaggageTariffService.getImportColumns().then(res => {
        setImportCols(res);
        setDisplayImportModal(true);
      });
    } else {
      setDisplayImportModal(true);
    }
  };
  const onImport = (file: RcFile): Promise<BaseImportResponse> => {
    return apiSdk.BaggageTariffService.import({
      file: file,
      fileName: file.name,
    });
  };
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                className="nta-create-btn"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  segmentForm.resetFields();
                  setDisplaySegmentModal(true);
                }}
              >
                {t('baggageTariffsPage_CreateNewSegment')}
              </Button>,
            ]
          : null
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/baggage-tariffs/get-baggage-tariffs"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={canUpdate}
          searchBtnAlignLeft
          onEdit={record => {
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
            });
          }}
          allowDelete={false}
          extraBtns={
            <Space>
              <ExportBtn
                disabled={!exportColumns?.length}
                onClick={() => {
                  toggleExportModal(true);
                }}
              />
              {canUpdate ? <ImportBtn onClick={handleImportClick} /> : null}
            </Space>
          }
          filters={filters}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            className="nta-custom-form"
            title={t('baggageTariffsPage_UpdateBaggageTariffs')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  updateEntity(values);
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>

                <Row gutter={ROW_GUTTER}>
                  <Col md={24}>
                    <Form.Item label={t('baggageTariffsPage_FreeBag')} name="freeBags" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item label={t('baggageTariffsPage_MaxBag')} name="maxBagsAllowed" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item
                      label={t('baggageTariffsPage_TarrifBagage')}
                      name="standardTariff"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item
                      label={t('baggageTariffsPage_SecondTariff')}
                      name="secondTariff"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item
                      label={t('baggageTariffsPage_TarrifBagageMoto')}
                      name="bikeTariff"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item
                      label={t('baggageTariffsPage_TicketClass')}
                      name="baseTicketClassId"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <CompanyTicketClassInput />
                    </Form.Item>
                  </Col>
                  <Col md={12}></Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
      {displaySegmentModal ? (
        <CreateSegmentModal
          onClose={reload => {
            setDisplaySegmentModal(false);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
      {showExportModal ? (
        <ExportColumnsSelectorModal
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
      {displayImportModal ? (
        <ImportFileModal
          sampleDownloadUrl="/ExcelTemplates/ImportExcelFormatBaggageTarrif.xlsx"
          cols={importCols}
          onImport={onImport}
          onClose={reload => {
            setDisplayImportModal(false);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
    </MainContent>
  );
};
export default BaggageTariffsPage;
